import React, { useState } from 'react';
import { useParams, Link } from 'react-router';
import { Button, CardFooter, useApi } from '@axiom/ui';
import type { CandidateWorkFeedItem, Candidate } from '@axiom/validation';

import { EnvUtil } from '../../utils/env-util';
import { CandidateOpportunitiesLegacyApi } from '../../api/candidate-opportunities-legacy-api';
import { WorkFeedApi } from '../../api/work-feed-api';
import type { WorkFeedViewsType } from '../../utils/WorkFeedUtil';
import { WorkFeedUtil } from '../../utils/WorkFeedUtil';
import { WorkFeedRemoveSavedOpportunityModal } from '../WorkFeedRemoveSavedOpportunityModal/WorkFeedRemoveSavedOpportunityModal';
import { WorkFeedHideOpportunityModal } from '../WorkFeedHideOpportunityModal/WorkFeedHideOpportunityModal';
import { ExpressInterestFlow } from '../ExpressInterestFlow/ExpressInterestFlow';

export type WorkFeedCardFooterProps = {
  workFeedItem: CandidateWorkFeedItem;
  view: WorkFeedViewsType;
  candidate: Candidate;
  name?: string;
};

export const WorkFeedCardFooter = ({
  workFeedItem,
  view,
  candidate,
  name = 'WORK_FEED_CARD_FOOTER',
}: WorkFeedCardFooterProps) => {
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const [isExpressInterestFlowOpen, setIsExpressInterestFlowOpen] =
    useState(false);
  const [isRemoveOpportunityModalOpen, setIsRemoveOpportunityModalOpen] =
    useState(false);
  const params = useParams();
  const isSPV = WorkFeedUtil.isSinglePageView(view);
  const {
    isInterested,
    isSubmitted,
    isClosed,
    isSelected,
    isFulfillmentCompleted,
  } = WorkFeedUtil.getWorkFeedCandidateStatuses(workFeedItem);

  const [{ interestedCount }] = useApi(
    WorkFeedApi.readWorkFeedInterestedCount(candidate.id)
  );

  const onSaveOpportunity = async () => {
    if (workFeedItem.isSaved) {
      setIsRemoveOpportunityModalOpen(true);
    } else {
      await CandidateOpportunitiesLegacyApi.createWorkFeedSaved(candidate.id, {
        opportunityId: workFeedItem.opportunity.id,
      });
      await WorkFeedApi.refreshAllOfWorkFeed(
        candidate.id,
        WorkFeedUtil.isSinglePageView(view)
          ? workFeedItem.opportunity.id
          : undefined,
        true
      );
      WorkFeedUtil.addSavedToasts(
        `${params.pageNumber ? '../../../' : '../'}saved`
      );
    }
  };

  const onRemoveOpportunity = async () => {
    if (workFeedItem.candidateIsInterested || workFeedItem.isSaved) {
      if (workFeedItem.candidateIsInterested) {
        await CandidateOpportunitiesLegacyApi.deleteWorkFeedInterested(
          candidate.id,
          workFeedItem.opportunity.id
        );
      }

      if (workFeedItem.isSaved) {
        await CandidateOpportunitiesLegacyApi.deleteWorkFeedSaved(
          candidate.id,
          workFeedItem.opportunity.id
        );
      }

      await WorkFeedApi.refreshAllOfWorkFeed(
        candidate.id,
        WorkFeedUtil.isSinglePageView(view)
          ? workFeedItem.opportunity.id
          : undefined,
        true
      );
    }
  };

  // Default Buttons
  if (!isInterested && !isClosed && !isSubmitted && !isFulfillmentCompleted) {
    return (
      <CardFooter name={name} stackableOn="mobile">
        {WorkFeedUtil.isAllView(view) && (
          <div>
            <Button
              name="PENDO_OPP_FEED_HIDE_BUTTON"
              variation="outline"
              onClick={() => {
                setIsHideModalOpen(true);
              }}
            >
              Hide
            </Button>
            {isHideModalOpen ? (
              <WorkFeedHideOpportunityModal
                name="WORK_FEED_HIDE_OPPORTUNITY_MODAL"
                onClose={() => {
                  setIsHideModalOpen(false);
                }}
                candidateId={candidate.id}
                opportunityId={workFeedItem.opportunity.id}
              />
            ) : null}
          </div>
        )}

        <div>
          <Button
            className={workFeedItem.isSaved ? 'favorited' : ''}
            name="HEART_BUTTON"
            variation="outline"
            onClick={onSaveOpportunity}
            icon={workFeedItem.isSaved ? 'heart-filled' : 'heart'}
          >
            {workFeedItem.isSaved ? 'Saved' : 'Save'}
          </Button>
          {isRemoveOpportunityModalOpen ? (
            <WorkFeedRemoveSavedOpportunityModal
              onClose={() => {
                setIsRemoveOpportunityModalOpen(false);
              }}
              candidateId={candidate.id}
              opportunityId={workFeedItem.opportunity.id}
              view={view}
            />
          ) : null}
        </div>

        {interestedCount < EnvUtil.workFeedMaxInterestedLimit &&
        !workFeedItem.opportunity.isExcludedFromFeed &&
        !!(workFeedItem.position.description ?? '').trim() &&
        !!workFeedItem.opportunity.allowInterest &&
        !WorkFeedUtil.isUnavailableToCandidate(workFeedItem) &&
        ((workFeedItem.opportunity.selfService &&
          WorkFeedUtil.hasRateValuesForSelfService(candidate)) ||
          !workFeedItem.opportunity.selfService) ? (
          <div>
            <Button
              name="CTA_EXPRESS_INTEREST"
              onClick={() => {
                setIsExpressInterestFlowOpen(true);
              }}
            >
              {workFeedItem.opportunity.selfService
                ? 'Submit to Client'
                : "I'm Interested"}
            </Button>
            {isExpressInterestFlowOpen ? (
              <ExpressInterestFlow
                candidate={candidate}
                onClose={() => {
                  setIsExpressInterestFlowOpen(false);
                }}
                workFeedItem={workFeedItem}
                view={view}
              />
            ) : null}
          </div>
        ) : null}
      </CardFooter>
    );
  }

  // View Submissions Button
  else if ((isSubmitted && !isClosed) || isSelected) {
    return (
      <CardFooter name={name}>
        <Link to={`../../${workFeedItem.opportunity.id}`}>
          <Button name="CTA_VIEW_SUBMISSION">View Submission</Button>
        </Link>
      </CardFooter>
    );
  }

  // Remove Interest Button
  else if (!isSPV && (isClosed || isFulfillmentCompleted)) {
    return (
      <CardFooter name={name}>
        <Button name="CTA_REMOVE_INTEREST" onClick={onRemoveOpportunity}>
          Remove
        </Button>
      </CardFooter>
    );
  }

  return null;
};
